import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { DependentsList, idadeDe } from "../components/DependentsPanel";
import { toast } from "sonner";
import axios from "axios";
import { formatDateBR } from "../utils/dateUtils";
import {
  Plus,
  Baby,
  Search,
  UserCheck,
  UserPlus,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Heart,
  Check,
  X,
  Share2,
  Users,
  UserCog,
  Filter,
  ChevronDown,
  Footprints,
  CheckCircle2,
  Circle,
  CheckCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const situacaoOptions = [
  "Sou Membro da Life",
  "Aceitei Jesus",
  "Reconciliei",
  "Sou cristão e quero me unir à Life",
  "Sou cristão mas ainda não sou parte da Life",
  "Frequento eventualmente"
];

const batismoOptions = [
  "Já sou batizado",
  "Desejo me batizar",
  "Nenhuma das opções"
];

const estadoCivilOptions = ["Casado(a)", "Solteiro(a)", "Viúvo(a)", "Divorciado(a)", "Amasiado(a)"];

const initialFormState = {
  nome_completo: "",
  email: "",
  telefone: "",
  endereco: "",
  cidade: "",
  sexo: "",
  data_nascimento: "",
  estado_civil: "",
  situacao: "",
  batismo: "",
  ministerios_desejados: [],
  funcoes_desejadas: [],
  aprovado_membro: false,
  aprovado_funcao: false
};

export default function PeoplePage() {
  const { user, isAdmin, currentBranch, branches } = useAuth();
  const [people, setPeople] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [profiles, setProfiles] = useState([]);

  // Helper functions to check specific permissions
  const canApproveMember = () => {
    if (isAdmin()) return true;
    return user?.permissoes?.aprovar_membro === true;
  };

  const canApproveFunction = () => {
    if (isAdmin()) return true;
    return user?.permissoes?.aprovar_funcao === true;
  };
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [selectedPersonForUser, setSelectedPersonForUser] = useState(null);
  const [editingPerson, setEditingPerson] = useState(null);
  const [responsavelSearch, setResponsavelSearch] = useState("");
  const [form, setForm] = useState(initialFormState);
  const [userForm, setUserForm] = useState({ senha: "", perfil_acesso_id: "" });
  const [activeTab, setActiveTab] = useState("todos");
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedForShare, setSelectedForShare] = useState(null);
  
  // Filtros avançados
  const [filterSituacao, setFilterSituacao] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterMinisterio, setFilterMinisterio] = useState("");
  const [filterFuncao, setFilterFuncao] = useState("");
  const [filterProjeto, setFilterProjeto] = useState("");
  const [filterProjetoTipo, setFilterProjetoTipo] = useState("inscrito"); // inscrito ou nao_inscrito
  const [showFilters, setShowFilters] = useState(false);
  
  // Projetos e Ministérios do líder
  const [projects, setProjects] = useState([]);
  const [myLedMinistries, setMyLedMinistries] = useState([]);
  const [myLedProjects, setMyLedProjects] = useState([]);
  const [projectParticipants, setProjectParticipants] = useState([]);
  
  // Verificar se usuário pode ver todos ou só liderados
  const canSeeAll = user?.permissoes?.pessoas?.ver_todos === true;
  
  // Trilho do Reino
  const [trilhoEtapas, setTrilhoEtapas] = useState([]);
  const [trilhoProgresso, setTrilhoProgresso] = useState([]);
  const [showTrilhoSection, setShowTrilhoSection] = useState(false);

  useEffect(() => {
    loadData();
  }, [currentBranch]);

  const loadData = async () => {
    try {
      const filialParam = currentBranch ? `?filial_id=${currentBranch.id}` : '';
      const [peopleRes, ministriesRes, functionsRes, profilesRes, etapasRes, projectsRes, myLeadershipRes] = await Promise.all([
        axios.get(`${API}/people${filialParam}`),
        axios.get(`${API}/ministries${filialParam}`),
        axios.get(`${API}/functions${filialParam}`),
        axios.get(`${API}/access-profiles`),
        axios.get(`${API}/trilho/etapas`, { params: { filial_id: currentBranch?.id } }).catch(() => ({ data: [] })),
        axios.get(`${API}/projects${filialParam}`).catch(() => ({ data: [] })),
        axios.get(`${API}/people/my-leadership`).catch(() => ({ data: { ministries: [], projects: [] } }))
      ]);
      setPeople(peopleRes.data);
      setMinistries(ministriesRes.data);
      setFunctions(functionsRes.data);
      setProfiles(profilesRes.data);
      setTrilhoEtapas(etapasRes.data || []);
      setProjects(projectsRes.data || []);
      setMyLedMinistries(myLeadershipRes.data?.ministries || []);
      setMyLedProjects(myLeadershipRes.data?.projects || []);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...form, filial_id: currentBranch?.id || null };
      if ((form.dependente || form.responsavel_id) && !form.responsavel_id) return toast.error("Selecione o responsável do dependente");
      if (editingPerson) {
        await axios.put(`${API}/people/${editingPerson.id}`, data);
        toast.success("Pessoa atualizada!");
      } else {
        await axios.post(`${API}/people`, data);
        toast.success("Pessoa cadastrada!");
      }
      setDialogOpen(false);
      setEditingPerson(null);
      setForm(initialFormState);
      loadData();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(x => x.msg || JSON.stringify(x)).join("; ") : (typeof d === "string" ? d : "Erro ao salvar"));
    }
  };

  const handleEdit = (person) => {
    setEditingPerson(person);
    setForm({
      ...person,
      ministerios_desejados: person.ministerios_desejados || (person.ministerio_desejado ? [person.ministerio_desejado] : []),
      funcoes_desejadas: person.funcoes_desejadas || (person.funcao_desejada ? [person.funcao_desejada] : [])
    });
    setShowTrilhoSection(false);
    // Load trilho progress for this person
    loadTrilhoProgresso(person.id);
    setDialogOpen(true);
  };

  const loadTrilhoProgresso = async (pessoaId) => {
    try {
      const res = await axios.get(`${API}/trilho/progresso/pessoa/${pessoaId}`);
      setTrilhoProgresso(res.data || []);
    } catch (error) {
      console.error("Error loading trilho progress:", error);
      setTrilhoProgresso([]);
    }
  };

  const handleAddTrilhoEtapa = async (etapaId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!editingPerson) return;
    try {
      await axios.post(`${API}/trilho/progresso`, {
        pessoa_id: editingPerson.id,
        etapa_id: etapaId
      });
      toast.success("Etapa registrada!");
      loadTrilhoProgresso(editingPerson.id);
      // Recarregar dados da pessoa para atualizar tags
      const res = await axios.get(`${API}/people/${editingPerson.id}`);
      setEditingPerson(res.data);
      // Recarregar lista de pessoas
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao registrar etapa");
    }
  };

  const handleRemoveTrilhoEtapa = async (progressoId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!window.confirm("Deseja remover esta etapa do progresso?")) return;
    try {
      await axios.delete(`${API}/trilho/progresso/${progressoId}`);
      toast.success("Etapa removida");
      loadTrilhoProgresso(editingPerson.id);
      // Recarregar dados da pessoa para atualizar tags
      const res = await axios.get(`${API}/people/${editingPerson.id}`);
      setEditingPerson(res.data);
      // Recarregar lista de pessoas
      loadData();
    } catch (error) {
      toast.error("Erro ao remover etapa");
    }
  };

  const getEtapaNome = (etapaId) => {
    const etapa = trilhoEtapas.find(e => e.id === etapaId);
    return etapa?.nome || etapaId;
  };

  const isEtapaConcluida = (etapaId) => {
    return trilhoProgresso.some(p => p.etapa_id === etapaId);
  };

  const getProgressoByEtapa = (etapaId) => {
    return trilhoProgresso.find(p => p.etapa_id === etapaId);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja remover esta pessoa?")) return;
    try {
      await axios.delete(`${API}/people/${id}`);
      toast.success("Pessoa removida");
      loadData();
    } catch (error) {
      toast.error("Erro ao remover");
    }
  };

  const handleApproveMember = async (id) => {
    try {
      await axios.put(`${API}/people/${id}/approve-member`);
      toast.success("Membro aprovado!");
      loadData();
    } catch (error) {
      toast.error("Erro ao aprovar");
    }
  };

  const handleApproveFunction = async (id) => {
    try {
      await axios.put(`${API}/people/${id}/approve-function`);
      toast.success("Voluntário aprovado!");
      loadData();
    } catch (error) {
      toast.error("Erro ao aprovar");
    }
  };

  const canCreateUserFromPerson = () => {
    // Admin sempre pode
    if (user?.nivel_acesso === "admin") return true;
    // Verificar permissão do perfil
    const permissoes = user?.permissoes || {};
    return permissoes.criar_usuario_pessoa === true;
  };

  const handleShareClick = (person) => {
    setSelectedForShare(person);
    setShareDialogOpen(true);
  };

  const handleShare = async (branchId) => {
    if (!selectedForShare) return;
    try {
      await axios.post(`${API}/share/people/${selectedForShare.id}`, { branch_id: branchId });
      toast.success("Pessoa compartilhada com sucesso!");
      setShareDialogOpen(false);
      setSelectedForShare(null);
      loadData();
    } catch (error) {
      toast.error("Erro ao compartilhar");
    }
  };

  const handleUnshare = async (branchId) => {
    if (!selectedForShare) return;
    try {
      await axios.delete(`${API}/share/people/${selectedForShare.id}/${branchId}`);
      toast.success("Compartilhamento removido!");
      loadData();
      // Atualizar o selectedForShare
      setSelectedForShare(prev => ({
        ...prev,
        compartilhado_com: prev.compartilhado_com.filter(id => id !== branchId)
      }));
    } catch (error) {
      toast.error("Erro ao remover compartilhamento");
    }
  };

  const handleOpenCreateUser = (person) => {
    setSelectedPersonForUser(person);
    setUserForm({ senha: "123456", perfil_acesso_id: "" });
    setCreateUserDialogOpen(true);
  };

  const handleCreateUserFromPerson = async () => {
    if (!selectedPersonForUser) return;
    try {
      await axios.post(`${API}/people/${selectedPersonForUser.id}/create-user`, {
        senha: userForm.senha,
        perfil_acesso_id: userForm.perfil_acesso_id || null
      });
      toast.success("Usuário criado com sucesso!");
      setCreateUserDialogOpen(false);
      setSelectedPersonForUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar usuário");
    }
  };

  const toggleMinistry = (ministryId) => {
    const isSelected = form.ministerios_desejados.includes(ministryId);
    if (isSelected) {
      setForm({
        ...form,
        ministerios_desejados: form.ministerios_desejados.filter(id => id !== ministryId)
      });
    } else {
      setForm({
        ...form,
        ministerios_desejados: [...form.ministerios_desejados, ministryId]
      });
    }
  };

  const toggleFunction = (functionId) => {
    const isSelected = form.funcoes_desejadas.includes(functionId);
    if (isSelected) {
      setForm({
        ...form,
        funcoes_desejadas: form.funcoes_desejadas.filter(id => id !== functionId)
      });
    } else {
      setForm({
        ...form,
        funcoes_desejadas: [...form.funcoes_desejadas, functionId]
      });
    }
  };

  const getAvailableFunctions = () => {
    if (form.ministerios_desejados.length === 0) return [];
    return functions.filter(f => 
      f.ministerios_ids?.some(mid => form.ministerios_desejados.includes(mid)) ||
      form.ministerios_desejados.includes(f.ministerio_id)
    );
  };

  const getMinistryName = (id) => ministries.find(m => m.id === id)?.nome || id;
  const getFunctionName = (id) => functions.find(f => f.id === id)?.nome || id;

  // Calcular estatísticas e aplicar filtros
  const { filteredPeople, stats } = useMemo(() => {
    // Calcular estatísticas gerais
    const totalGeral = people.length;
    const totalMembros = people.filter(p => p.aprovado_membro).length;
    const totalVoluntarios = people.filter(p => p.aprovado_funcao).length;
    
    // Aplicar filtros
    let filtered = people.filter(p => {
      // Filtro de busca
      const matchSearch = p.nome_completo?.toLowerCase().includes(search.toLowerCase()) ||
                         p.email?.toLowerCase().includes(search.toLowerCase());
      if (!matchSearch) return false;
      
      // Filtro de situação
      if (filterSituacao && p.situacao !== filterSituacao) return false;
      
      // Filtro de ministério
      if (filterMinisterio) {
        const personMinistries = p.ministerios_desejados || (p.ministerio_desejado ? [p.ministerio_desejado] : []);
        if (!personMinistries.includes(filterMinisterio)) return false;
      }
      
      // Filtro de função
      if (filterFuncao) {
        const personFunctions = p.funcoes_desejadas || (p.funcao_desejada ? [p.funcao_desejada] : []);
        if (!personFunctions.includes(filterFuncao)) return false;
      }
      
      // Filtro de status
      if (filterStatus) {
        const hasMinistries = (p.ministerios_desejados?.length > 0) || p.ministerio_desejado;
        
        switch (filterStatus) {
          case "validar_membro":
            // Pessoas não aprovadas como membro
            if (p.aprovado_membro) return false;
            break;
          case "validar_voluntario":
            // Pessoas com ministério desejado mas não aprovadas como voluntário
            if (!hasMinistries || p.aprovado_funcao) return false;
            break;
          case "membro":
            // Pessoas aprovadas como membro
            if (!p.aprovado_membro) return false;
            break;
          case "usuario_sistema":
            // Pessoas que têm user_id (acesso ao sistema)
            if (!p.user_id) return false;
            break;
          default:
            break;
        }
      }
      
      // Filtro de abas
      const hasMinistries = (p.ministerios_desejados?.length > 0) || p.ministerio_desejado;
      if (activeTab === "pendente_membro" && p.aprovado_membro) return false;
      if (activeTab === "pendente_voluntario" && (!hasMinistries || p.aprovado_funcao)) return false;
      
      // Filtro por projeto - só aplica se tiver projeto selecionado E participantes carregados
      if (filterProjeto) {
        // Se ainda não carregou os participantes, não filtra (mostra todos temporariamente)
        if (projectParticipants.length === 0 && filterProjetoTipo === "inscrito") {
          // Aguardando carregar participantes - não mostra ninguém ainda
          return false;
        }
        const isInProject = projectParticipants.some(pp => pp.pessoa_id === p.id);
        if (filterProjetoTipo === "inscrito" && !isInProject) return false;
        if (filterProjetoTipo === "nao_inscrito" && isInProject) return false;
      }
      
      return true;
    });
    
    return {
      filteredPeople: filtered,
      stats: {
        totalGeral,
        totalMembros,
        totalVoluntarios,
        totalFiltrado: filtered.length
      }
    };
  }, [people, search, filterSituacao, filterStatus, filterMinisterio, filterFuncao, activeTab, filterProjeto, filterProjetoTipo, projectParticipants]);
  
  // Carregar participantes do projeto selecionado
  useEffect(() => {
    if (filterProjeto) {
      console.log("Carregando participantes do projeto:", filterProjeto);
      axios.get(`${API}/social/project-participants/${filterProjeto}`)
        .then(res => {
          console.log("Participantes carregados:", res.data);
          setProjectParticipants(res.data || []);
        })
        .catch((err) => {
          console.error("Erro ao carregar participantes:", err);
          setProjectParticipants([]);
        });
    } else {
      setProjectParticipants([]);
    }
  }, [filterProjeto]);
  
  // Ministérios disponíveis para filtro (todos para admin, só liderados para líder)
  const availableMinistries = useMemo(() => {
    if (canSeeAll) return ministries;
    return myLedMinistries.length > 0 ? myLedMinistries : ministries;
  }, [canSeeAll, ministries, myLedMinistries]);

  // Projetos disponíveis para filtro de líderes
  // Mostra apenas projetos com visivel_filtro_pessoas=true E onde o usuário é líder
  // Projetos disponíveis para filtro de líderes
  // Se usuário é líder de QUALQUER ministério ou projeto, vê todos os projetos com visivel_filtro_pessoas=true
  const availableProjects = useMemo(() => {
    const isAdmin = user?.nivel_acesso === "admin";
    const hasVerTodos = user?.permissoes?.social?.ver_todos === true;
    const isAnyLeader = myLedMinistries.length > 0 || myLedProjects.length > 0;
    
    // Admin, quem tem permissão ver_todos, ou qualquer líder vê todos os projetos com a flag ativa
    if (isAdmin || hasVerTodos || isAnyLeader) {
      return projects.filter(p => p.visivel_filtro_pessoas === true);
    }
    
    // Usuário comum não vê nenhum projeto no filtro
    return [];
  }, [projects, myLedProjects, myLedMinistries, user?.permissoes?.social?.ver_todos, user?.nivel_acesso]);

  // Opções de status para filtro
  const statusOptions = [
    { value: "validar_membro", label: "Validar Membro", description: "Aguardando aprovação como membro" },
    { value: "validar_voluntario", label: "Validar Voluntário", description: "Aguardando aprovação como voluntário" },
    { value: "membro", label: "Membro", description: "Membros aprovados" },
    { value: "usuario_sistema", label: "Usuário de Sistema", description: "Com acesso ao sistema" }
  ];

  // Limpar filtros
  const clearFilters = () => {
    setFilterSituacao("");
    setFilterStatus("");
    setFilterMinisterio("");
    setFilterFuncao("");
    setFilterProjeto("");
    setFilterProjetoTipo("inscrito");
    setSearch("");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="pessoas-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Pessoas</h1>
        <Button onClick={() => { setEditingPerson(null); setForm(initialFormState); setDialogOpen(true); }} data-testid="add-person-button">
          <Plus className="h-4 w-4 mr-2" />
          Adicionar
        </Button>
      </div>

      {/* Resumo de Estatísticas */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/20 border-blue-200 dark:border-blue-800">
          <CardContent className="p-3 text-center">
            <Users className="h-5 w-5 mx-auto mb-1 text-blue-600 dark:text-blue-400" />
            <p className="text-2xl font-bold text-blue-700 dark:text-blue-300" data-testid="total-geral">{stats.totalGeral}</p>
            <p className="text-xs text-blue-600 dark:text-blue-400">Total Geral</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/30 dark:to-green-900/20 border-green-200 dark:border-green-800">
          <CardContent className="p-3 text-center">
            <UserCheck className="h-5 w-5 mx-auto mb-1 text-green-600 dark:text-green-400" />
            <p className="text-2xl font-bold text-green-700 dark:text-green-300" data-testid="total-membros">{stats.totalMembros}</p>
            <p className="text-xs text-green-600 dark:text-green-400">Membros</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/30 dark:to-purple-900/20 border-purple-200 dark:border-purple-800">
          <CardContent className="p-3 text-center">
            <Heart className="h-5 w-5 mx-auto mb-1 text-purple-600 dark:text-purple-400" />
            <p className="text-2xl font-bold text-purple-700 dark:text-purple-300" data-testid="total-voluntarios">{stats.totalVoluntarios}</p>
            <p className="text-xs text-purple-600 dark:text-purple-400">Voluntários</p>
          </CardContent>
        </Card>
      </div>

      {/* Barra de Busca e Filtros */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome ou email..."
              className="pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-people"
            />
          </div>
          <Button
            variant={showFilters ? "default" : "outline"}
            size="icon"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters"
          >
            <Filter className="h-4 w-4" />
          </Button>
        </div>

        {/* Painel de Filtros */}
        {showFilters && (
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Filtros Avançados
              </h3>
              {(filterSituacao || filterStatus || filterMinisterio || filterFuncao) && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  Limpar Filtros
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Filtro por Situação */}
              <div className="space-y-2">
                <Label className="text-sm">Situação</Label>
                <Select value={filterSituacao || "all"} onValueChange={(v) => setFilterSituacao(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-situacao">
                    <SelectValue placeholder="Todas as situações" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="max-h-[200px]">
                    <SelectItem value="all">Todas as situações</SelectItem>
                    {situacaoOptions.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro por Status */}
              <div className="space-y-2">
                <Label className="text-sm">Status</Label>
                <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-status">
                    <SelectValue placeholder="Todos os status" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="max-h-[200px]">
                    <SelectItem value="all">Todos os status</SelectItem>
                    {statusOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex flex-col">
                          <span>{opt.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro por Ministério */}
              <div className="space-y-2">
                <Label className="text-sm">Ministério</Label>
                <Select value={filterMinisterio || "all"} onValueChange={(v) => setFilterMinisterio(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-ministerio">
                    <SelectValue placeholder="Todos os ministérios" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="max-h-[200px]">
                    <SelectItem value="all">Todos os ministérios</SelectItem>
                    {availableMinistries.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro por Função */}
              <div className="space-y-2">
                <Label className="text-sm">Função</Label>
                <Select value={filterFuncao || "all"} onValueChange={(v) => setFilterFuncao(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-funcao">
                    <SelectValue placeholder="Todas as funções" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="max-h-[200px]">
                    <SelectItem value="all">Todas as funções</SelectItem>
                    {functions.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Filtro por Projeto (Inscrição/Social) */}
              <div className="space-y-2">
                <Label className="text-sm">Projeto</Label>
                <div className="flex gap-2">
                  <Select 
                    value={filterProjeto || "all"} 
                    onValueChange={(v) => setFilterProjeto(v === "all" ? "" : v)}
                    disabled={availableProjects.length === 0}
                  >
                    <SelectTrigger data-testid="filter-projeto" className="flex-1">
                      <SelectValue placeholder={availableProjects.length === 0 ? "Nenhum projeto disponível" : "Selecione um projeto"} />
                    </SelectTrigger>
                    <SelectContent position="popper" className="max-h-[200px]">
                      <SelectItem value="all">Todos</SelectItem>
                      {availableProjects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {filterProjeto && (
                    <Select value={filterProjetoTipo} onValueChange={setFilterProjetoTipo}>
                      <SelectTrigger data-testid="filter-projeto-tipo" className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inscrito">Inscrito</SelectItem>
                        <SelectItem value="nao_inscrito">Não inscrito</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
                {availableProjects.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Nenhum projeto com "Visível no Filtro de Pessoas" ativo
                  </p>
                )}
              </div>
            </div>

            {/* Contador de resultados */}
            {(filterSituacao || filterStatus || filterMinisterio || filterFuncao || filterProjeto || search) && (
              <div className="mt-3 pt-3 border-t">
                <Badge variant="secondary" className="text-sm">
                  {stats.totalFiltrado} resultado{stats.totalFiltrado !== 1 ? 's' : ''} encontrado{stats.totalFiltrado !== 1 ? 's' : ''}
                </Badge>
              </div>
            )}
          </Card>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="todos" data-testid="tab-todos">Todos</TabsTrigger>
          <TabsTrigger value="pendente_membro" data-testid="tab-pendente-membro" className="text-xs sm:text-sm">
            Pendente | Membro
          </TabsTrigger>
          <TabsTrigger value="pendente_voluntario" data-testid="tab-pendente-voluntario" className="text-xs sm:text-sm">
            Pendente | Voluntário
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <div className="space-y-3">
            {filteredPeople.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Nenhuma pessoa encontrada</p>
            ) : (
              filteredPeople.map((person) => (
                <Card key={person.id} className="card-hover" data-testid={`person-card-${person.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold truncate">{person.nome_completo}</h3>
                          {person.aprovado_membro ? (
                            <Badge className="badge-success text-xs">Membro</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">Pendente</Badge>
                          )}
                          {person.aprovado_funcao && (
                            <Badge className="bg-primary/10 text-primary text-xs">Voluntário</Badge>
                          )}
                          {person.dependente && (
                            <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 text-xs gap-1" data-testid={`person-dependente-badge-${person.id}`}>
                              <Baby className="h-3 w-3" />{person.ministerio_infantil_id ? "Criança" : "Dependente"}{person.responsavel_nome ? ` de ${person.responsavel_nome.split(" ")[0]}` : ""}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="space-y-1 text-sm text-muted-foreground">
                          {person.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="h-3 w-3" />
                              <span className="truncate">{person.email}</span>
                            </div>
                          )}
                          {person.telefone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-3 w-3" />
                              <span>{person.telefone}</span>
                            </div>
                          )}
                          {person.cidade && (
                            <div className="flex items-center gap-2">
                              <MapPin className="h-3 w-3" />
                              <span>{person.cidade}</span>
                            </div>
                          )}
                          {people.some(d => d.responsavel_id === person.id) && (
                            <div className="flex items-center gap-2 flex-wrap" data-testid={`person-dependents-${person.id}`}>
                              <Baby className="h-3 w-3 text-sky-600" />
                              <span>Dependentes:</span>
                              {people.filter(d => d.responsavel_id === person.id).map(d => (
                                <Badge key={d.id} className="text-xs bg-sky-100 text-sky-700 hover:bg-sky-100 cursor-pointer" onClick={() => handleEdit(d)}>
                                  {d.nome_completo.split(" ")[0]}{d.data_nascimento ? ` · ${idadeDe(d.data_nascimento)} anos` : ""}
                                </Badge>
                              ))}
                            </div>
                          )}
                          {(person.ministerios_desejados?.length > 0 || person.ministerio_desejado) && (
                            <div className="flex items-center gap-2 flex-wrap">
                              <Heart className="h-3 w-3" />
                              <span>Deseja servir: </span>
                              {(person.ministerios_desejados || [person.ministerio_desejado].filter(Boolean)).map(mid => (
                                <Badge key={mid} variant="secondary" className="text-xs">
                                  {getMinistryName(mid)}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col gap-1 ml-2">
                        <Button size="icon" variant="ghost" onClick={() => handleEdit(person)} data-testid={`edit-person-${person.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {branches.length > 1 && (
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            onClick={() => handleShareClick(person)} 
                            title="Compartilhar com outra unidade"
                            data-testid={`share-person-${person.id}`}
                          >
                            <Share2 className="h-4 w-4" />
                          </Button>
                        )}
                        {canCreateUserFromPerson() && !person.user_id && person.email && (
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="text-blue-500" 
                            onClick={() => handleOpenCreateUser(person)} 
                            title="Criar usuário do sistema"
                            data-testid={`create-user-${person.id}`}
                          >
                            <UserPlus className="h-4 w-4" />
                          </Button>
                        )}
                        {/* Admin-only buttons */}
                        {isAdmin() && (
                          <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDelete(person.id)} data-testid={`delete-person-${person.id}`} title="Excluir">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* Show shared info */}
                    {person.compartilhado_com?.length > 0 && (
                      <div className="mt-2 pt-2 border-t flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-muted-foreground">Compartilhado com:</span>
                        {person.compartilhado_com.map(branchId => {
                          const branch = branches.find(b => b.id === branchId);
                          return branch ? (
                            <Badge key={branchId} variant="outline" className="text-xs">
                              {branch.nome}
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{editingPerson ? "Editar Pessoa" : "Nova Pessoa"}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            <form onSubmit={handleSubmit} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nome Completo *</Label>
                <Input
                  required
                  value={form.nome_completo}
                  onChange={(e) => setForm({ ...form, nome_completo: e.target.value })}
                  data-testid="person-nome"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={form.email || ""}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="person-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input
                    value={form.telefone || ""}
                    onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                    data-testid="person-telefone"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Endereço</Label>
                <Input
                  value={form.endereco || ""}
                  onChange={(e) => setForm({ ...form, endereco: e.target.value })}
                  data-testid="person-endereco"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cidade</Label>
                  <Input
                    value={form.cidade || ""}
                    onChange={(e) => setForm({ ...form, cidade: e.target.value })}
                    data-testid="person-cidade"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sexo</Label>
                  <Select
                    value={form.sexo || ""}
                    onValueChange={(v) => setForm({ ...form, sexo: v })}
                  >
                    <SelectTrigger data-testid="person-sexo">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="masculino">Masculino</SelectItem>
                      <SelectItem value="feminino">Feminino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data de Nascimento</Label>
                  <Input
                    type="date"
                    value={form.data_nascimento || ""}
                    onChange={(e) => setForm({ ...form, data_nascimento: e.target.value })}
                    data-testid="person-nascimento"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Estado Civil</Label>
                  <Select
                    value={form.estado_civil || ""}
                    onValueChange={(v) => setForm({ ...form, estado_civil: v })}
                  >
                    <SelectTrigger data-testid="person-estado-civil">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {estadoCivilOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Situação</Label>
                <Select
                  value={form.situacao || ""}
                  onValueChange={(v) => setForm({ ...form, situacao: v })}
                >
                  <SelectTrigger data-testid="person-situacao">
                    <SelectValue placeholder="Selecione sua situação" />
                  </SelectTrigger>
                  <SelectContent>
                    {situacaoOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Dependentes desta pessoa (quando ela é a responsável) */}
              {!(form.dependente || form.responsavel_id) && (
                <div className="p-3 rounded-lg border bg-muted/20" data-testid="person-dependents-panel">
                  {editingPerson ? (
                    <DependentsList responsavelId={editingPerson.id} responsavelTelefone={form.telefone} compact onChanged={loadData} />
                  ) : (
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Baby className="h-3.5 w-3.5 text-sky-600" /> Salve a pessoa para cadastrar os dependentes (crianças) dela.</p>
                  )}
                </div>
              )}

              {/* Dependente (criança) de outra pessoa */}
              <div className="space-y-3 p-3 rounded-lg bg-sky-50 border border-sky-100" data-testid="dependent-section">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="flex items-center gap-1.5"><Baby className="h-4 w-4 text-sky-600" /> Dependente de outra pessoa</Label>
                    <p className="text-[11px] text-muted-foreground">Crianças até 11 anos entram automaticamente no Ministério Infantil</p>
                  </div>
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-sky-600"
                    checked={!!form.responsavel_id || !!form.dependente}
                    onChange={(e) => setForm({ ...form, dependente: e.target.checked, responsavel_id: e.target.checked ? form.responsavel_id : null, responsavel_nome: e.target.checked ? form.responsavel_nome : null })}
                    data-testid="person-dependente-check"
                  />
                </div>
                {(form.dependente || form.responsavel_id) && (
                  <>
                    <div className="space-y-1.5">
                      <Label>Responsável *</Label>
                      {form.responsavel_id ? (
                        <div className="flex items-center justify-between p-2 rounded-md bg-background border text-sm">
                          <span data-testid="person-responsavel-nome">{form.responsavel_nome || people.find(p => p.id === form.responsavel_id)?.nome_completo || "Selecionado"}</span>
                          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setForm({ ...form, responsavel_id: null, responsavel_nome: null })}>trocar</Button>
                        </div>
                      ) : (
                        <>
                          <Input placeholder="Buscar responsável pelo nome…" value={responsavelSearch} onChange={(e) => setResponsavelSearch(e.target.value)} data-testid="person-responsavel-search" />
                          {responsavelSearch.trim().length >= 2 && (
                            <div className="max-h-36 overflow-y-auto rounded-md border bg-background divide-y">
                              {people.filter(p => p.id !== editingPerson?.id && !p.dependente && p.nome_completo.toLowerCase().includes(responsavelSearch.toLowerCase())).slice(0, 8).map(p => (
                                <button type="button" key={p.id} className="w-full text-left px-3 py-2 text-sm hover:bg-muted" onClick={() => { setForm({ ...form, dependente: true, responsavel_id: p.id, responsavel_nome: p.nome_completo, telefone: form.telefone || p.telefone || "" }); setResponsavelSearch(""); }} data-testid={`responsavel-option-${p.id}`}>
                                  {p.nome_completo} <span className="text-xs text-muted-foreground">{p.telefone}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Alergia a medicamentos/alimentos</Label>
                        <Input value={form.alergias || ""} onChange={(e) => setForm({ ...form, alergias: e.target.value })} placeholder="Ex.: dipirona, amendoim ou 'nenhuma'" data-testid="person-alergias" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Condição especial</Label>
                        <Input value={form.condicao_especial || ""} onChange={(e) => setForm({ ...form, condicao_especial: e.target.value })} placeholder="Ex.: TEA, TDAH, asma…" data-testid="person-condicao" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Observações</Label>
                      <Input value={form.observacoes_dependente || ""} onChange={(e) => setForm({ ...form, observacoes_dependente: e.target.value })} data-testid="person-obs-dependente" />
                    </div>
                  </>
                )}
              </div>

              <div className="space-y-2">
                <Label>Batismo</Label>
                <Select
                  value={form.batismo || ""}
                  onValueChange={(v) => setForm({ ...form, batismo: v })}
                >
                  <SelectTrigger data-testid="person-batismo">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {batismoOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Múltipla seleção de Ministérios */}
              <div className="space-y-2">
                <Label>Deseja servir em quais Ministérios?</Label>
                <div className="border rounded-lg p-3 space-y-2 bg-muted/30">
                  <div className="flex flex-wrap gap-2">
                    {ministries.map((m) => {
                      const isSelected = form.ministerios_desejados.includes(m.id);
                      return (
                        <Badge
                          key={m.id}
                          variant={isSelected ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => toggleMinistry(m.id)}
                          data-testid={`ministry-${m.id}`}
                        >
                          {m.nome}
                          {isSelected && <X className="h-3 w-3 ml-1" />}
                        </Badge>
                      );
                    })}
                  </div>
                  {form.ministerios_desejados.length === 0 && (
                    <p className="text-xs text-muted-foreground">Clique para selecionar ministérios</p>
                  )}
                </div>
              </div>

              {/* Múltipla seleção de Funções */}
              {form.ministerios_desejados.length > 0 && (
                <div className="space-y-2">
                  <Label>Funções Desejadas</Label>
                  <div className="border rounded-lg p-3 space-y-2 bg-muted/30">
                    <div className="flex flex-wrap gap-2">
                      {getAvailableFunctions().map((f) => {
                        const isSelected = form.funcoes_desejadas.includes(f.id);
                        return (
                          <Badge
                            key={f.id}
                            variant={isSelected ? "default" : "outline"}
                            className="cursor-pointer"
                            onClick={() => toggleFunction(f.id)}
                            data-testid={`function-${f.id}`}
                          >
                            {f.nome}
                            {isSelected && <X className="h-3 w-3 ml-1" />}
                          </Badge>
                        );
                      })}
                      {getAvailableFunctions().length === 0 && (
                        <p className="text-xs text-muted-foreground">Nenhuma função disponível para os ministérios selecionados</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Status de Aprovação - Informativo */}
              {editingPerson && (
                <div className="flex items-center gap-4 pt-2 text-sm text-muted-foreground">
                  {form.aprovado_membro && (
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Membro Aprovado
                    </Badge>
                  )}
                  {form.aprovado_funcao && (
                    <Badge className="bg-blue-100 text-blue-700">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Voluntário Aprovado
                    </Badge>
                  )}
                  {!form.aprovado_membro && !form.aprovado_funcao && (
                    <span className="text-xs italic">
                      A aprovação é gerenciada pelo Trilho do Reino
                    </span>
                  )}
                </div>
              )}

              {/* Trilho do Reino - Apenas para edição */}
              {editingPerson && trilhoEtapas.length > 0 && (
                <div className="space-y-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowTrilhoSection(!showTrilhoSection)}
                    className="flex items-center gap-2 w-full text-left font-medium text-primary"
                    data-testid="toggle-trilho-section"
                  >
                    <Footprints className="h-5 w-5" />
                    <span>Trilho do Reino</span>
                    <Badge variant="secondary" className="ml-auto">
                      {trilhoProgresso.length}/{trilhoEtapas.length}
                    </Badge>
                    <ChevronDown className={`h-4 w-4 transition-transform ${showTrilhoSection ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showTrilhoSection && (
                    <div className="space-y-2 pl-2">
                      {/* Progress bar */}
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-3">
                        <div 
                          className="h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all"
                          style={{ width: `${trilhoEtapas.length > 0 ? (trilhoProgresso.length / trilhoEtapas.length) * 100 : 0}%` }}
                        />
                      </div>
                      
                      {trilhoEtapas.map((etapa, index) => {
                        const concluida = isEtapaConcluida(etapa.id);
                        const progresso = getProgressoByEtapa(etapa.id);
                        
                        return (
                          <div 
                            key={etapa.id}
                            className={`flex items-center gap-3 p-3 rounded-lg border ${
                              concluida ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                            }`}
                            data-testid={`trilho-etapa-${etapa.id}`}
                          >
                            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                              concluida ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                            } text-sm font-bold`}>
                              {concluida ? <Check className="h-4 w-4" /> : index + 1}
                            </div>
                            <div className="flex-1">
                              <p className={`font-medium ${concluida ? 'text-green-700' : 'text-gray-700'}`}>
                                {etapa.nome}
                              </p>
                              {progresso?.data_conclusao && (
                                <p className="text-xs text-green-600">
                                  Concluído em {new Date(progresso.data_conclusao).toLocaleDateString('pt-BR')}
                                </p>
                              )}
                            </div>
                            {concluida ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleRemoveTrilhoEtapa(progresso.id, e)}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                data-testid={`remove-etapa-${etapa.id}`}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={(e) => handleAddTrilhoEtapa(etapa.id, e)}
                                className="text-green-600 border-green-300 hover:bg-green-50"
                                data-testid={`add-etapa-${etapa.id}`}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" />
                                Marcar
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1" data-testid="save-person-button">
                  Salvar
                </Button>
              </div>
            </form>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Dialog para criar usuário a partir de pessoa */}
      <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              Criar Usuário do Sistema
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedPersonForUser && (
              <>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="font-medium">{selectedPersonForUser.nome_completo}</p>
                  <p className="text-sm text-muted-foreground">{selectedPersonForUser.email}</p>
                </div>

                <div className="space-y-2">
                  <Label>Senha Inicial</Label>
                  <Input
                    type="password"
                    value={userForm.senha}
                    onChange={(e) => setUserForm({ ...userForm, senha: e.target.value })}
                    placeholder="Senha inicial do usuário"
                    data-testid="create-user-senha"
                  />
                  <p className="text-xs text-muted-foreground">O usuário poderá alterar depois</p>
                </div>

                <div className="space-y-2">
                  <Label>Perfil de Acesso</Label>
                  <Select
                    value={userForm.perfil_acesso_id || "none"}
                    onValueChange={(v) => setUserForm({ ...userForm, perfil_acesso_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="create-user-perfil">
                      <SelectValue placeholder="Selecione um perfil" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <span className="text-muted-foreground">Nenhum perfil</span>
                      </SelectItem>
                      {profiles.map((profile) => (
                        <SelectItem key={profile.id} value={profile.id}>
                          {profile.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setCreateUserDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button className="flex-1" onClick={handleCreateUserFromPerson} data-testid="confirm-create-user">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Criar Usuário
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Compartilhar Pessoa</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Compartilhe <strong>{selectedForShare?.nome_completo}</strong> com outra unidade.
            </p>
            
            {selectedForShare?.compartilhado_com?.length > 0 && (
              <div className="space-y-2">
                <Label>Já compartilhado com:</Label>
                <div className="flex flex-wrap gap-2">
                  {selectedForShare.compartilhado_com.map(branchId => {
                    const branch = branches.find(b => b.id === branchId);
                    return branch ? (
                      <Badge key={branchId} variant="secondary" className="gap-1">
                        {branch.nome}
                        <button 
                          onClick={() => handleUnshare(branchId)}
                          className="ml-1 hover:text-destructive"
                        >
                          ×
                        </button>
                      </Badge>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Compartilhar com:</Label>
              <div className="space-y-2">
                {branches
                  .filter(b => b.id !== currentBranch?.id && !selectedForShare?.compartilhado_com?.includes(b.id))
                  .map(branch => (
                    <Button
                      key={branch.id}
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => handleShare(branch.id)}
                    >
                      <Share2 className="h-4 w-4 mr-2" />
                      {branch.nome}
                      {branch.tipo === "instituto_social" && (
                        <Badge variant="secondary" className="ml-2">Instituto</Badge>
                      )}
                    </Button>
                  ))}
                {branches.filter(b => b.id !== currentBranch?.id && !selectedForShare?.compartilhado_com?.includes(b.id)).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Pessoa já compartilhada com todas as unidades disponíveis.
                  </p>
                )}
              </div>
            </div>

            <Button variant="outline" className="w-full" onClick={() => setShareDialogOpen(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
